import React, {useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PropTypes from 'prop-types';
import PauseIcon from '@mui/icons-material/Pause';
import {procedureNodeTypeEnum, runProcedureInEnum} from "../../../serverClientShared/sharedEnums";
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import {CircularProgress, MenuItem, Select, Tooltip} from "@mui/material";
import ConditionalComponent from "../../Shared/ConditionalComponent";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useStores} from "../../../Hooks";
import Utility from "../../../Utility";
import ProcedureUtility from "../../../Utilities/ProcedureUtility";
import {isIOS, isMacOs, isWindows} from "react-device-detect";

const PlayerMenu = observer(({isPreview, isStartNode}) => {
    const {procedureStore, generalStore, dataStore, projectStore, modalStore} = useStores();
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [isBeginLoading, setIsBeginLoading] = useState(false);

    const currentNode = (isPreview ? procedureStore.currentPreviewedNode : procedureStore.currentNode) || {};
    const currentProcedure = (isPreview ? procedureStore.currentPreviewedProcedure : procedureStore.currentProcedure) || {};
    const nextStepAvailable = isPreview ? procedureStore.nextPreviewedStepAvailable : procedureStore.nextStepAvailable;
    const currentStep = isPreview ? procedureStore.currentPreviewedStep : procedureStore.currentStep;

    const isEndNode = currentNode.NodeType === procedureNodeTypeEnum.END;
    const isFinalNode = !isEndNode && checkIsFinalNode();
    const clickEndDisabled = procedureStore.sessionViewerOpen;
    const isPreviousInactive = isEndNode || (currentStep <= 1 && !procedureStore.innerProcedurePreviousStepsIds.length);
    const is2D = !currentProcedure.AllowRun || currentProcedure.AllowRun === runProcedureInEnum.BOTH || currentProcedure.AllowRun === runProcedureInEnum.TWO_D;
    const currentHas3D = !isPreview ? procedureStore.currentHas3D : procedureStore.currentHas3DPreview;
    const selectedModelName = procedureStore.selected3DModel?.ProcedureName;
    const projectProceduresMap = dataStore.projectsProceduresMap.get(projectStore.currentProject?.ProjectIdentifier);


    useEffect(() => {
        if (currentHas3D) {
            if (!projectProceduresMap) {
                projectStore.setCurrentProjectDetails(currentProcedure.ProjectRef, true).catch(() => setIsDataFetched(true));
                procedureStore.getAttached3dItems(currentProcedure.ThreeDModelsConnected).then(missing3dModels => {
                    procedureStore.connected3DModels = [...missing3dModels];
                    procedureStore.selected3DModel = procedureStore.connected3DModels[0];
                    setIsDataFetched(true);
                });
            } else if (projectProceduresMap) {
                const fetched3DModels = [];
                const missingItemIds = [];
                currentProcedure.ThreeDModelsConnected?.forEach(modelId => {
                    const connectedItem = projectProceduresMap.get(modelId);
                    if (connectedItem && ProcedureUtility.is3dFileAttached(connectedItem)) {
                        fetched3DModels.push(connectedItem);
                    } else {
                        missingItemIds.push(modelId);
                    }
                });

                procedureStore.getAttached3dItems(missingItemIds).then(missing3dModels => {
                    procedureStore.connected3DModels = [...fetched3DModels, ...missing3dModels];
                    procedureStore.selected3DModel = procedureStore.connected3DModels[0];
                    setIsDataFetched(true);
                });
            }
        } else {
            setIsDataFetched(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProcedure.ProjectRef, currentHas3D, projectProceduresMap, currentProcedure.ThreeDModelsConnected]);

    useEffect(()=>{
        if(currentNode.NodeType === procedureNodeTypeEnum.OBJECT_DETECTION) {
            goToNext();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentNode])


    let hasNonApprovedPopup = false;

    if (currentNode && currentNode.WarningPopupIdentifiers && currentNode.WarningPopupIdentifiers.length) {
        for (const nodeId of currentNode.WarningPopupIdentifiers) {
            const popupNode = procedureStore.getNodeById(nodeId, isPreview);
            if (!!popupNode && !popupNode.approved && !!popupNode.RequireConformation) {
                hasNonApprovedPopup = true;
                break;
            }
        }
    }

    let isNextInactive = hasNonApprovedPopup || isEndNode || !nextStepAvailable;
    if (isNextInactive && procedureStore.innerProcedureNextStepIds.length) {
        const inactiveDependOnSelection = [procedureNodeTypeEnum.MULTI, procedureNodeTypeEnum.QUESTION].includes(currentNode.NodeType);
        if (inactiveDependOnSelection) {
            isNextInactive = !currentNode.userSelection;
        } else {
            isNextInactive = false;
        }
    }

    function pauseSession() {
        procedureStore.togglePauseProcedure(isPreview);
    }


    async function goToPrevious(currentStep) {
        const goToPreviousMethod = isPreview ? procedureStore.goToPreviousPreviewed : procedureStore.goToPrevious;
        if (currentStep > 0) {
            goToPreviousMethod()
        } else if (procedureStore.innerProcedurePreviousStepsIds.length) {
            await procedureStore.goBackToParentProcedure(isPreview);
        }
    }

    async function goToNext() {
        const goToNextMethod = isPreview ? procedureStore.goToNextPreviewed : procedureStore.goToNext;
        const goToNodeMethod = isPreview ? procedureStore.goToPreviewNode : procedureStore.goToNode;
        const defaultNode = isPreview ? procedureStore.defaultPreviewedNode : procedureStore.defaultNode;
        if (!nextStepAvailable) {
            if (procedureStore.innerProcedureNextStepIds.length) {
                await procedureStore.goNextToParentProcedure(isPreview);
            }
            return;
        }
        if ([procedureNodeTypeEnum.START, procedureNodeTypeEnum.SINGLE, procedureNodeTypeEnum.WARNING].includes(currentNode.NodeType)) {
            goToNextMethod(0)
        } else if (currentNode.userSelection) {
            await goToNodeMethod(currentNode.userSelection)
        } else {
            await goToNodeMethod(defaultNode)
        }
        for (const nodeId of currentNode.WarningPopupIdentifiers) {
            const popupNode = procedureStore.getNodeById(nodeId, isPreview);
            if (!popupNode) {
                continue;
            }
            popupNode.approved= false;
        }
    }

    function endProcedure() {
        procedureStore.endProcedure(isPreview)
    }

    async function beginProcedure(runIn3D = false) {
        if (isBeginLoading) return;
        if (!!runIn3D) {
            if (isPreview) {
                modalStore.showInfoMessage(generalStore.translate('cannotRun') + ' 3D ' + generalStore.translate('inPreviewSetting', true));
            } else {
                procedureStore.runProcedureIn3D = true;
                const runOnThisDevice = (!!procedureStore.selected3DModel.ThreeDFilesWin && isWindows) ||
                    (!!procedureStore.selected3DModel.ThreeDFilesAndroid && Utility.isAndroid()) ||
                    (!!procedureStore.selected3DModel.ThreeDFilesIOs && isIOS) ||
                    (!!procedureStore.selected3DModel.ThreeDFilesOsx && isMacOs);
                const canDownloadAndRunInApp = !procedureStore.play3d && runOnThisDevice;
                const runOnWeb = !Utility.isMobile() && !!procedureStore.selected3DModel.ThreeDFilesWebgl;

                if (runOnWeb && !canDownloadAndRunInApp) {
                    if (generalStore.zoomLevel >= 105 || generalStore.zoomLevel <= 95) {
                        modalStore.isZoomWarningModalOpen = true;
                    } else
                        procedureStore.play3d = true;
                }
            }
        }
        if (!runIn3D) {
            setIsBeginLoading(true);
            try {
                await procedureStore.beginSession(isPreview);
                if (!procedureStore.resumedSession) {
                    await goToNext();
                }
            } catch (error) {
                console.error('Failed to begin procedure:', error);
            } finally {
                setIsBeginLoading(false);
            }
        }
    }

    function checkIsFinalNode() {
        if (procedureStore.innerProcedureNextStepIds.length) {
            return false;
        }
        let isFinalNode = false;
        if (!currentNode || !currentNode.ConnectionsData || currentNode.ConnectionsData.length === 0) {
            isFinalNode = true;
        }
        if (!!currentNode && !!currentNode.ConnectionsData && currentNode.ConnectionsData.length === 1) {
            isFinalNode = currentNode.ConnectionsData[0].ConnectedTo === ""
        }
        if (!!currentNode && !!currentNode.ConnectionsData && currentNode.ConnectionsData.length > 1) {
            isFinalNode = currentNode.userSelection && currentNode.userSelection.split("_")[0] === "procedure-end"
        }
        if (isPreview && procedureStore.currentPreviewedProcedure.isFakeProcedure) {
            isFinalNode = false;
        }
        return isFinalNode
    }

    function renderActionButtons() {
        const canRun3D = !!currentHas3D && !!selectedModelName && !isPreview;
        return (
            <ConditionalComponent if={!!isStartNode}>
                <ConditionalComponent if={isDataFetched && !isFinalNode}>
                    <ConditionalComponent if={is2D}>
                        <div className="player-menu-container start-procedure">
                            <div
                                className={"start-procedure-btn footer-action-button" + (isNextInactive || isBeginLoading ? ' inactive' : '')}
                                style={isBeginLoading ? {pointerEvents: 'none'} : {}}
                                onClick={() => !isNextInactive && beginProcedure()}
                            >
                                <span style={isBeginLoading ? {visibility: 'hidden'} : {}}>
                                    {is2D ? generalStore.translate("runIn") + ' 2D' : generalStore.translate("startProcedure")}
                                </span>
                                {isBeginLoading && <CircularProgress size={20} style={{color: 'inherit', position: 'absolute'}}/>}
                            </div>
                        </div>
                    </ConditionalComponent>
                    <ConditionalComponent if={!!currentHas3D && !!is2D}>
                        <div style={{width: '20px'}}/>
                    </ConditionalComponent>
                    <ConditionalComponent if={canRun3D}>
                        <div className="player-menu-container start-procedure">
                            <div
                                className={"start-procedure-btn footer-action-button" + (isNextInactive || isBeginLoading ? ' inactive' : '')}
                                style={isBeginLoading ? {pointerEvents: 'none'} : {}}
                                onClick={() => !isBeginLoading && beginProcedure(true)}
                            >
                                {canRun3D ? generalStore.translate("runIn") + ' 3D' : generalStore.translate("startProcedure")}
                            </div>
                            <ConditionalComponent if={procedureStore.connected3DModels?.length > 1}>
                                <Select
                                    className='select connected-model-select'
                                    value={procedureStore.selected3DModel?.ProcedureIdentifier}
                                    disableUnderline
                                    IconComponent={ExpandMoreIcon}
                                    MenuProps={{
                                        getContentAnchorEl: null,
                                        anchorOrigin: {
                                            vertical: "bottom",
                                            horizontal: "center",
                                        }
                                    }}
                                >
                                    {procedureStore.connected3DModels?.map(model => {
                                        const modelId = model.ProcedureIdentifier;

                                        return (
                                            <MenuItem
                                                className='connected-model-list-item'
                                                key={modelId}
                                                value={modelId}
                                                onClick={() => {
                                                    procedureStore.selected3DModel = model;
                                                }}
                                            >
                                                <div
                                                    className='connected-model-procedure-name'>{model.ProcedureName}</div>
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            </ConditionalComponent>
                        </div>


                    </ConditionalComponent>
                </ConditionalComponent>
            </ConditionalComponent>
        );
    }

    return (
        <React.Fragment>
            <ConditionalComponent if={!isStartNode}>
                <div className="player-menu-container">
                    <div
                        className={"previous-node-btn footer-action-button" + (isPreviousInactive ? ' inactive' : '')}
                        onClick={() => !isPreviousInactive && goToPrevious(currentStep)}
                    >
                        <ArrowBackIcon fontSize="large"/>
                    </div>
                    <Tooltip title={isPreview ? generalStore.translate('noActiveSessionsInPreview') : ''}>
                        <div
                            className={"footer-action-button pause-procedure-btn" + (isEndNode || isPreview ? ' inactive' : '')}
                            onClick={() => !isEndNode && pauseSession()}>
                            <PauseIcon fontSize="large"/>
                        </div>
                    </Tooltip>
                    <ConditionalComponent if={!!isFinalNode}>
                        <Tooltip title={generalStore.translate('endCurrentProcedureSession')}>
                            <div
                                className={"next-node-btn footer-action-button end-procedure-tab" + (hasNonApprovedPopup ? ' inactive' : '') + (clickEndDisabled ? " click-disabled" : "")}
                                onClick={() => !hasNonApprovedPopup && !clickEndDisabled && endProcedure()}
                            >
                                <CancelPresentationIcon fontWeight={600} size="large"/>
                            </div>
                        </Tooltip>
                    </ConditionalComponent>

                    <ConditionalComponent if={!isFinalNode}>
                        <div
                            className={"next-node-btn footer-action-button" + (isNextInactive ? ' inactive' : '')}
                            onClick={() => !isNextInactive && goToNext()}
                        >
                            <ArrowForwardIcon fontSize="large"/>
                        </div>
                    </ConditionalComponent>
                </div>
            </ConditionalComponent>
            {renderActionButtons()}
        </React.Fragment>
    )
});

PlayerMenu.propTypes = {
    isPreview: PropTypes.bool.isRequired
}

export default PlayerMenu
